<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiText, UiButton, UiTextarea, UiAvatar, UiIcon } from '@shopexperts/shared';
import { useExpertNotes } from '@/app/composables/expert-manager/useExpertNotes';

const props = defineProps<{
  expertId: number;
}>();

const {
  notes,
  totalNotes,
  isLoading,
  error,
  refetch,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  createNote,
} = useExpertNotes(() => props.expertId);

const draft = ref('');
const isPending = computed(() => createNote.isPending.value);
const trimmedDraft = computed(() => draft.value.trim());
const isTooLong = computed(() => trimmedDraft.value.length > 5000);
const canSubmit = computed(() => trimmedDraft.value.length > 0 && !isTooLong.value && !isPending.value);

async function submit() {
  if (!canSubmit.value) return;
  await createNote.mutateAsync(trimmedDraft.value);
  draft.value = '';
}

function formatTimestamp(value: string | null) {
  if (!value) return '';
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
</script>

<template>
  <div class="space-y-6">
    <!-- Composer -->
    <div class="space-y-3">
      <UiText as="h3" variant="caption" class="tracking-wide text-gray-400">ADD A NOTE</UiText>
      <UiTextarea
        v-model="draft"
        placeholder="What should the next admin looking at this expert know?"
        :rows="3"
        :disabled="isPending"
        :invalid="isTooLong"
        :error="isTooLong ? `Notes are limited to 5000 characters (${trimmedDraft.length}).` : undefined"
      />
      <div class="flex items-center justify-between gap-3">
        <UiText as="span" variant="caption-normal" class="text-gray-400">
          Notes are internal, visible to admins only, and cannot be edited or deleted.
        </UiText>
        <UiButton
          variant="primary"
          size="md"
          :label="isPending ? 'Adding…' : 'Add note'"
          :disabled="!canSubmit"
          @click="submit"
        />
      </div>
    </div>

    <!-- Timeline -->
    <div v-if="isLoading" class="py-8 text-center">
      <UiText as="p" variant="p-small" class="text-gray-400">Loading notes...</UiText>
    </div>

    <!-- Must precede the empty state: an unavailable internal record read as
         "no notes yet" would be actively misleading. -->
    <div v-else-if="error" class="py-10 text-center">
      <UiIcon name="close" class="mx-auto mb-3 h-8 w-8 text-rose-300" />
      <UiText as="p" variant="p" class="text-gray-600">Couldn't load notes</UiText>
      <UiText as="p" variant="p-small" class="mt-1 text-gray-400">
        The timeline may be incomplete — this is not an empty record.
      </UiText>
      <UiButton variant="outline" size="md" label="Try again" class="mt-4" @click="refetch()" />
    </div>

    <div v-else-if="notes.length === 0" class="py-10 text-center">
      <UiIcon name="pencil" class="mx-auto mb-3 h-8 w-8 text-gray-300" />
      <UiText as="p" variant="p" class="text-gray-400">No notes yet</UiText>
    </div>

    <div v-else class="space-y-4">
      <UiText as="h3" variant="caption" class="tracking-wide text-gray-400">
        {{ totalNotes }} NOTE{{ totalNotes !== 1 ? 'S' : '' }}
      </UiText>

      <div
        v-for="note in notes"
        :key="note.id"
        class="flex gap-3 border-l-2 border-gray-100 pl-4"
      >
        <UiAvatar
          :name="note.author.name || note.author.email"
          :url="note.author.avatar ?? undefined"
          size="xs"
          :responsive="false"
          color-coded
          class="shrink-0"
        />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-x-2">
            <UiText as="span" variant="p-small" class="font-medium text-gray-800">
              {{ note.author.name || note.author.email }}
            </UiText>
            <UiText as="span" variant="caption-normal" class="text-gray-400">
              {{ formatTimestamp(note.created_at) }}
            </UiText>
          </div>
          <UiText as="p" variant="p-small" class="mt-1 whitespace-pre-wrap text-gray-600">
            {{ note.body }}
          </UiText>
        </div>
      </div>

      <div v-if="hasNextPage" class="pt-2">
        <UiButton
          variant="outline"
          size="md"
          :label="isFetchingNextPage ? 'Loading…' : 'Load older notes'"
          :disabled="isFetchingNextPage"
          @click="fetchNextPage()"
        />
      </div>
    </div>
  </div>
</template>
